import type { DocumentSnapshot, QuerySnapshot, DocumentData } from 'firebase/firestore';
import { parseDoc, punSchema, type Pun, type Reaction } from '../../shared/schemas';

/**
 * Turn a Firestore query snapshot into validated puns, dropping any documents
 * that cannot be parsed. Replaces the ad-hoc `docs.map(...)` + timestamp
 * juggling that was repeated in every component that read puns.
 */
export function punsFromSnapshot(
  snapshot: QuerySnapshot<DocumentData>,
  context: string,
): Pun[] {
  return snapshot.docs
    .map((doc) => parseDoc(punSchema, doc.id, doc.data(), context))
    .filter((pun): pun is Pun => pun !== null);
}

/** Parse a single pun document, or null if it does not exist or is malformed. */
export function punFromDoc(
  snapshot: DocumentSnapshot<DocumentData>,
  context: string,
): Pun | null {
  if (!snapshot.exists()) return null;
  return parseDoc(punSchema, snapshot.id, snapshot.data(), context);
}

/** How many users gave this pun a particular reaction. */
export function countReaction(pun: Pun, reaction: Reaction): number {
  return Object.values(pun.reactions).filter((list) => list[0] === reaction).length;
}

/** The reaction this user gave, if any. */
export function reactionBy(pun: Pun, userId: string | undefined): Reaction | undefined {
  return userId ? pun.reactions[userId]?.[0] : undefined;
}

/**
 * Most-crowned first, breaking ties with the most recent pun.
 * Returns a new array rather than sorting in place.
 */
export function sortPunsByCrowns(puns: Pun[]): Pun[] {
  return [...puns].sort((a, b) => {
    const crownDifference = countReaction(b, 'crown') - countReaction(a, 'crown');
    if (crownDifference !== 0) return crownDifference;
    return b.createdAt.getTime() - a.createdAt.getTime();
  });
}

/*
 * `hasUsedCrownOn` used to live here. It compared local-midnight boundaries,
 * which is the same timezone bug as gotcha 1, and its warning about the crown
 * limit being unenforced went stale when `setReaction` started writing
 * `dailyCrowns/{uid}_{day}` in the same transaction as the reaction.
 *
 * `usePunFeed` now derives the same flag from `pun.pstDay`, which is the day
 * the server actually recorded.
 */
